import {
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Stack,
  Typography,
} from '@mui/material';
import { styled } from '@mui/system';
import { motion } from 'framer-motion';
import { Phone } from 'lucide-react';
import { CallType, CallMode } from '../../routes/agent/RolePlay';

// Styled components
const CallAnimation = styled(motion.div)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  pointerEvents: 'none',
});

const AnimatedBox = styled(motion.div)({});

const CallHeader = styled(Box)(({ theme }) => ({
  background: '#C11919', // Prudential red
  color: 'white',
  padding: theme.spacing(4, 2, 6),
  position: 'relative',
  textAlign: 'center',
}));

const AvatarContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  marginTop: theme.spacing(-5),
  marginBottom: theme.spacing(2),
  position: 'relative',
  zIndex: 10,
}));

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  width: theme.spacing(12),
  height: theme.spacing(12),
  border: '4px solid white',
  boxShadow: theme.shadows[2],
}));

const ActionButton = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
}));

// Call badge colors
const getCallBadgeColor = (callType: CallType) => {
  return callType === CallType.INITIAL ? 'primary' : 'error';
};

const getCallBadgeText = (callType: CallType) => {
  return callType === CallType.INITIAL
    ? 'Initial Consultation'
    : 'Follow-up Consultation';
};

interface IncomingCallCardProps {
  agentInfo: any;
  callType: CallType;
  callMode?: CallMode;
  accentDisplay?: string;
  onAnswer: () => void;
  onDecline: () => void;
}

export const IncomingCallCard = ({
  agentInfo,
  callType,
  callMode,
  accentDisplay,
  onAnswer,
  onDecline,
}: IncomingCallCardProps) => {
  return (
    <Card
      component={motion.div}
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: 'spring', duration: 0.5 }}
      sx={{ maxWidth: 360, width: '100%', overflow: 'hidden' }}
    >
      <CallHeader>
        <CallAnimation
          animate={{ opacity: [0.2, 0.5, 0.2] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <svg
            width="240"
            height="240"
            viewBox="0 0 240 240"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <circle
              cx="120"
              cy="120"
              r="90"
              stroke="#FFFFFF"
              strokeOpacity="0.15"
              strokeWidth="4"
            />
            <circle
              cx="120"
              cy="120"
              r="60"
              stroke="#FFFFFF"
              strokeOpacity="0.2"
              strokeWidth="4"
            />
            <circle
              cx="120"
              cy="120"
              r="30"
              stroke="#FFFFFF"
              strokeOpacity="0.25"
              strokeWidth="4"
            />
          </svg>
        </CallAnimation>

        <Typography
          variant="h5"
          fontWeight="600"
          sx={{ mb: 0.5, position: 'relative' }}
        >
          {agentInfo?.profile?.name || 'Alex Tan'}
        </Typography>

        <Typography
          variant="body2"
          sx={{ color: 'rgba(255,255,255,0.8)', position: 'relative' }}
        >
          Prudential • Insurance
        </Typography>

        <Box
          sx={{
            position: 'absolute',
            top: 16,
            right: 16,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignItems: 'flex-end',
          }}
        >
          <Chip
            label={callMode === CallMode.SCRIPTED ? 'Annual Check-In' : getCallBadgeText(callType)}
            color={callMode === CallMode.SCRIPTED ? 'warning' : getCallBadgeColor(callType)}
            size="small"
          />
          {accentDisplay && (
            <Chip
              label={`${accentDisplay} Accent`}
              color="secondary"
              size="small"
              variant="outlined"
              sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}
            />
          )}
        </Box>
      </CallHeader>

      <AvatarContainer>
        <StyledAvatar
          src={
            agentInfo?.profile?.avatarUrl || agentInfo?.profile?.avatar ||
            'https://dopmo1eihgbgm.cloudfront.net/679127688b20f30484d209eb/alex-headshot.jpg'
          }
          alt={agentInfo?.profile?.name || 'Alex Tan'}
        />
      </AvatarContainer>

      <CardContent sx={{ textAlign: 'center' }}>
        <Typography
          variant="h6"
          color="error"
          sx={{ mb: 1 }}
          component={motion.div}
          animate={{ scale: [1, 1.03, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          Incoming Call
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          {callMode === CallMode.SCRIPTED
            ? 'Annual Policy Review Call'
            : callType === CallType.INITIAL
            ? 'Health Insurance Consultation'
            : 'Follow-up Consultation'}
        </Typography>

        <Stack
          direction="row"
          spacing={4}
          justifyContent="center"
          sx={{ mb: 1 }}
        >
          <ActionButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={onDecline}
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'grey.200',
                color: 'grey.700',
                mb: 1,
                '&:hover': {
                  bgcolor: 'grey.300',
                },
              }}
            >
              <Phone style={{ transform: 'rotate(135deg)' }} />
            </Button>
            <Typography variant="body2" color="text.secondary">
              Decline
            </Typography>
          </ActionButton>

          <ActionButton whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
            <Button
              onClick={onAnswer}
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                bgcolor: 'success.main',
                color: 'white',
                mb: 1,
                '&:hover': {
                  bgcolor: 'success.dark',
                },
              }}
              component={motion.button}
              animate={{
                boxShadow: [
                  '0 4px 6px rgba(76, 175, 80, 0.2)',
                  '0 4px 12px rgba(76, 175, 80, 0.4)',
                  '0 4px 6px rgba(76, 175, 80, 0.2)',
                ],
              }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <Phone />
            </Button>
            <Typography variant="body2" color="text.secondary">
              Answer
            </Typography>
          </ActionButton>
        </Stack>
      </CardContent>
    </Card>
  );
};
